import { Entity } from './Entity';
import { EntityList } from './Utils/EntityList';
import { IdentifierPool } from './Utils/IdentifierPool';
import { EntitySystem } from './Systems/EntitySystem';
import { ComponentStorageManager, ComponentRegistry, GlobalComponentRegistry } from './Core/ComponentStorage';
import { QuerySystem } from './Core/QuerySystem';
import { TypeSafeEventSystem } from './Core/EventSystem';
import { ReferenceTracker } from './Core/ReferenceTracker';
import { RuntimeConfig } from '../RuntimeConfig';
import { getComponentInstanceTypeName, getSystemInstanceTypeName, getSystemMetadata, getSystemInstanceMetadata } from './Decorators';
import { TypedQueryBuilder } from './Core/Query/TypedQuery';
import { SceneSerializer } from './Serialization/SceneSerializer';
import { IncrementalSerializer } from './Serialization/IncrementalSerializer';
import { ComponentPoolManager } from './Core/ComponentPool';
import { PerformanceMonitor } from '../Utils/PerformanceMonitor';
import { ProfilerSDK } from '../Utils/Profiler/ProfilerSDK';
import { ProfileCategory } from '../Utils/Profiler/ProfilerTypes';
import { AutoProfiler } from '../Utils/Profiler/AutoProfiler';
import { ServiceContainer } from '../Core/ServiceContainer';
import { createInstance, isInjectable, injectProperties } from '../Core/DI';
import { createLogger } from '../Utils/Logger';
import { SystemScheduler, CycleDependencyError } from './Core/SystemScheduler';
import { EntityHandleManager } from './Core/EntityHandleManager';
import { isValidHandle } from './Core/EntityHandle';
import { EpochManager } from './Core/EpochManager';
/**
 * 游戏场景默认实现类
 *
 * 实现IScene接口，提供场景的基础功能。
 * 推荐使用组合而非继承的方式来构建自定义场景。
 */
export class Scene {
    /**
     * @zh 获取运行时环境
     * @en Get runtime environment
     *
     * @zh 优先返回场景级别设置，否则返回 Core 全局设置
     * @en Returns scene-level setting if set, otherwise returns Core global setting
     */
    get runtimeEnvironment() {
        if (this._runtimeEnvironmentOverride) {
            return this._runtimeEnvironmentOverride;
        }
        return RuntimeConfig.runtimeEnvironment;
    }
    /**
     * @zh 是否在服务端运行
     * @en Whether running on server
     */
    get isServer() {
        return this.runtimeEnvironment === 'server';
    }
    /**
     * @zh 是否在客户端运行
     * @en Whether running on client
     */
    get isClient() {
        return this.runtimeEnvironment === 'client';
    }
    /**
     * 获取场景中所有已注册的EntitySystem
     *
     * 按updateOrder排序。使用缓存机制，仅在系统变化时重新排序。
     *
     * @returns 系统列表
     */
    get systems() {
        if (!this._systemsOrderDirty && this._cachedSystems) {
            return this._cachedSystems;
        }
        this._cachedSystems = this._rebuildSystemsCache();
        this._systemsOrderDirty = false;
        return this._cachedSystems;
    }
    /**
     * 重新构建系统缓存
     *
     * 从服务容器中提取所有EntitySystem并排序。
     * 使用 SystemScheduler 进行依赖排序，支持 before/after 声明。
     *
     * Rebuild system cache.
     * Extract all EntitySystems from service container and sort them.
     * Uses SystemScheduler for dependency sorting, supports before/after declarations.
     */
    _rebuildSystemsCache() {
        const allServices = this._services.getAll();
        const systems = this._filterEntitySystems(allServices);
        try {
            this._systemScheduler.markDirty();
            return this._systemScheduler.getAllSortedSystems(systems);
        }
        catch (error) {
            if (error instanceof CycleDependencyError) {
                this._logger.error(`[Scene] 系统存在循环依赖，回退到 updateOrder 排序 | Cycle dependency detected, falling back to updateOrder sort`, error.involvedNodes);
            }
            else {
                this._logger.error(`[Scene] 系统排序失败 | System sorting failed`, error);
            }
            return this._sortSystemsByUpdateOrder(systems);
        }
    }
    /**
     * 从服务列表中过滤出EntitySystem实例
     */
    _filterEntitySystems(services) {
        return services.filter((service) => service instanceof EntitySystem);
    }
    /**
     * 按 updateOrder 排序系统，相同时按 addOrder 保证稳定性
     * Sort systems by updateOrder, use addOrder as secondary key for stability
     */
    _sortSystemsByUpdateOrder(systems) {
        return systems.sort((a, b) => {
            const orderDiff = a.updateOrder - b.updateOrder;
            if (orderDiff !== 0)
                return orderDiff;
            return a.addOrder - b.addOrder;
        });
    }
    /**
     * 通过类型获取System实例
     *
     * @param systemType System类型
     * @returns System实例，如果未找到则返回null
     *
     * @example
     * ```typescript
     * const physics = scene.getSystem(PhysicsSystem);
     * if (physics) {
     *     physics.doSomething();
     * }
     * ```
     */
    getSystem(systemType) {
        return this._services.tryResolve(systemType);
    }
    /**
     * 标记系统顺序为脏
     *
     * 当系统列表或顺序发生变化时调用，使缓存失效
     */
    markSystemsOrderDirty() {
        this._systemsOrderDirty = true;
    }
    /**
     * 获取场景的服务容器
     *
     * 用于注册和解析场景级别的服务（如EntitySystem）。
     *
     * @example
     * ```typescript
     * // 注册服务
     * scene.services.registerSingleton(PhysicsSystem);
     *
     * // 解析服务
     * const physics = scene.services.resolve(PhysicsSystem);
     * ```
     */
    get services() {
        return this._services;
    }
    /**
     * @zh 创建场景实例
     * @en Create scene instance
     *
     * @param config - @zh 场景配置 @en Scene configuration
     */
    constructor(config) {
        /**
         * 场景名称
         *
         * 用于标识和调试的友好名称。
         */
        this.name = '';
        /**
         * 场景自定义数据
         *
         * 用于存储场景级别的配置和状态数据。
         */
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        this.sceneData = new Map();
        /**
         * 句柄到实体的映射
         *
         * 用于 O(1) 时间复杂度的句柄查找。
         *
         * Handle to entity mapping.
         * Used for O(1) lookup by handle.
         */
        this._handleToEntity = new Map();
        /**
         * Epoch 管理器
         *
         * 用于帧级变更检测，追踪组件的修改时间。
         *
         * Epoch manager.
         * Used for frame-level change detection, tracking component modification time.
         */
        this.epochManager = new EpochManager();
        /**
         * 性能监控器缓存
         *
         * 用于监控场景和系统的性能。从 ServiceContainer 获取。
         */
        this._performanceMonitor = null;
        /**
         * 场景是否已开始运行
         */
        this._didSceneBegin = false;
        /**
         * 编辑器模式标志
         *
         * 当为 true 时，组件的生命周期回调（如 onAddedToEntity）会被延迟，
         * 直到调用 begin() 开始运行场景时才会触发。
         *
         * Editor mode flag.
         * When true, component lifecycle callbacks (like onAddedToEntity) are deferred
         * until begin() is called to start running the scene.
         */
        this.isEditorMode = false;
        /**
         * 延迟的组件生命周期回调队列
         *
         * 在编辑器模式下，组件的 onAddedToEntity 回调会被加入此队列，
         * 等到 begin() 调用时统一执行。
         *
         * Deferred component lifecycle callback queue.
         * In editor mode, component's onAddedToEntity callbacks are queued here,
         * and will be executed when begin() is called.
         */
        this._deferredComponentCallbacks = [];
        /**
         * 系统列表缓存
         */
        this._cachedSystems = null;
        /**
         * 系统顺序脏标记
         *
         * 当系统增删或 updateOrder 改变时标记为 true，下次访问 systems 时重新构建缓存
         */
        this._systemsOrderDirty = true;
        /**
         * 系统错误计数器
         *
         * 跟踪每个系统的错误次数，用于自动禁用频繁出错的系统
         */
        this._systemErrorCount = new Map();
        /**
         * 系统添加计数器
         *
         * 用于为每个添加的系统分配唯一的添加顺序，保证排序稳定性
         * Counter for assigning unique add order to each system for stable sorting
         */
        this._systemAddCounter = 0;
        /**
         * 系统调度器
         *
         * 用于管理系统的执行阶段和依赖关系。
         * 支持 before/after 依赖声明和拓扑排序。
         *
         * System scheduler.
         * Manages system execution stages and dependencies.
         * Supports before/after dependency declarations and topological sorting.
         */
        this._systemScheduler = new SystemScheduler();
        /**
         * 组件ID到系统的索引映射
         *
         * 用于快速查找关心特定组件的系统，避免遍历所有系统。
         * 使用组件ID（数字）而非ComponentType作为key，避免类引用问题。
         *
         * Component ID to systems index map.
         * Used for fast lookup of systems that care about specific components.
         * Uses component ID (number) instead of ComponentType as key to avoid class reference issues.
         */
        this._componentIdToSystems = new Map();
        /**
         * 需要接收所有组件变化通知的系统集合
         *
         * 包括使用 none 条件、tag/name 查询、或空匹配器的系统。
         * 这些系统无法通过组件ID索引优化，需要在每次组件变化时都检查。
         *
         * Systems that need to receive all component change notifications.
         * Includes systems using none conditions, tag/name queries, or empty matchers.
         * These systems cannot be optimized via component ID indexing.
         */
        this._globalNotifySystems = new Set();
        this.entities = new EntityList(this);
        this.identifierPool = new IdentifierPool();
        this.componentStorageManager = new ComponentStorageManager();
        this.componentRegistry = new ComponentRegistry();
        if (config?.inheritGlobalRegistry !== false) {
            this.componentRegistry.cloneFrom(GlobalComponentRegistry);
        }
        this.querySystem = new QuerySystem();
        this.eventSystem = new TypeSafeEventSystem();
        this.referenceTracker = new ReferenceTracker();
        this.handleManager = new EntityHandleManager();
        this._services = new ServiceContainer();
        this._logger = createLogger('Scene');
        this._maxErrorCount = config?.maxSystemErrorCount ?? 10;
        // 只有显式指定时才覆盖，否则从 Core 读取
        if (config?.runtimeEnvironment) {
            this._runtimeEnvironmentOverride = config.runtimeEnvironment;
        }
        if (config?.name) {
            this.name = config.name;
        }
    }
    /**
     * 获取性能监控器
     *
     * 从 ServiceContainer 获取，如果未注册则创建默认实例（向后兼容）
     */
    get performanceMonitor() {
        if (!this._performanceMonitor) {
            this._performanceMonitor = this._services.tryResolve(PerformanceMonitor) ?? new PerformanceMonitor();
        }
        return this._performanceMonitor;
    }
    /**
     * 初始化场景
     *
     * 在场景创建时调用，子类可以重写此方法来设置初始实体和组件。
     */
    initialize() { }
    /**
     * 场景开始运行时的回调
     *
     * 在场景开始运行时调用，可以在此方法中执行场景启动逻辑。
     */
    onStart() { }
    /**
     * 场景卸载时的回调
     *
     * 在场景被销毁时调用，可以在此方法中执行清理工作。
     */
    unload() { }
    /**
     * 添加延迟的组件生命周期回调
     *
     * 在编辑器模式下，组件的 onAddedToEntity 回调会通过此方法加入队列。
     *
     * Queue a deferred component lifecycle callback.
     * In editor mode, component's onAddedToEntity callbacks are queued via this method.
     *
     * @param callback 要延迟执行的回调 | The callback to defer
     */
    queueDeferredComponentCallback(callback) {
        this._deferredComponentCallbacks.push(callback);
    }
    /**
     * 开始场景，启动实体处理器等
     *
     * 这个方法会启动场景。它将启动实体处理器等，并调用onStart方法。
     * 在编辑器模式下，此方法还会执行所有延迟的组件生命周期回调。
     *
     * This method starts the scene. It will start entity processors and call onStart.
     * In editor mode, this method also executes all deferred component lifecycle callbacks.
     */
    begin() {
        this._didSceneBegin = true;
        if (this._deferredComponentCallbacks.length > 0) {
            for (const callback of this._deferredComponentCallbacks) {
                try {
                    callback();
                }
                catch (error) {
                    this._logger.error('Error executing deferred component callback:', error);
                }
            }
            this._deferredComponentCallbacks = [];
        }
        this.onStart();
    }
    /**
     * 结束场景，清除实体、实体处理器等
     *
     * 这个方法会结束场景。它将移除所有实体，结束实体处理器等，并调用unload方法。
     *
     * 执行顺序：
     * 1. 调用 unload() - 用户可以在此访问实体和系统进行清理
     * 2. 清理所有实体
     * 3. 清空服务容器，触发所有系统的 onDestroy()
     *
     * 注意：
     * - onRemoved 回调不会在 Scene.end() 时触发，因为这是批量销毁场景
     * - 用户清理：在 Scene.unload() 中处理（可访问实体和系统）
     * - 系统清理：在 System.onDestroy() 中处理（实体已被清理）
     */
    end() {
        this._didSceneBegin = false;
        this.unload();
        this.entities.removeAllEntities();
        this.querySystem.setEntities([]);
        this.componentStorageManager.clear();
        this._services.clear();
        this._cachedSystems = null;
        this._systemsOrderDirty = true;
        this._componentIdToSystems.clear();
        this._globalNotifySystems.clear();
        this._handleToEntity.clear();
        this.handleManager.reset();
        this.epochManager.reset();
    }
    /**
     * @zh 更新场景
     * @en Update scene
     * @internal 由 SceneManager 或 World 调用，用户不应直接调用
     */
    update() {
        this.epochManager.increment();
        ProfilerSDK.beginFrame();
        const frameHandle = ProfilerSDK.beginSample('Scene.update', ProfileCategory.ECS);
        try {
            ComponentPoolManager.getInstance().update();
            this.entities.updateLists();
            const systems = this.systems;
            this._runSystemPhase(systems, 'update', 'Systems.update');
            this._runSystemPhase(systems, 'lateUpdate', 'Systems.lateUpdate');
            this.flushCommandBuffers(systems);
        }
        finally {
            ProfilerSDK.endSample(frameHandle);
            ProfilerSDK.endFrame();
        }
    }
    /**
     * @zh 执行系统的指定阶段
     * @en Run specified phase for all systems
     */
    _runSystemPhase(systems, phase, profileName) {
        const phaseHandle = ProfilerSDK.beginSample(profileName, ProfileCategory.ECS);
        try {
            for (const system of systems) {
                if (!this._shouldSystemRun(system))
                    continue;
                const suffix = phase === 'lateUpdate' ? '.late' : '';
                const systemHandle = ProfilerSDK.beginSample(`${system.systemName}${suffix}`, ProfileCategory.ECS);
                try {
                    system[phase]();
                }
                catch (error) {
                    this._handleSystemError(system, phase, error);
                }
                finally {
                    ProfilerSDK.endSample(systemHandle);
                }
            }
        }
        finally {
            ProfilerSDK.endSample(phaseHandle);
        }
    }
    _shouldSystemRun(system) {
        if (!system.enabled)
            return false;
        if (!this.isEditorMode)
            return true;
        const metadata = getSystemInstanceMetadata(system);
        return metadata?.runInEditMode !== false;
    }
    /**
     * 执行所有系统的延迟命令
     * Flush all systems' deferred commands
     *
     * 在帧末统一执行所有通过 CommandBuffer 提交的延迟操作。
     * Execute all deferred operations submitted via CommandBuffer at end of frame.
     */
    flushCommandBuffers(systems) {
        const flushHandle = ProfilerSDK.beginSample('Scene.flushCommandBuffers', ProfileCategory.ECS);
        try {
            for (const system of systems) {
                try {
                    system.flushCommands();
                }
                catch (error) {
                    this._logger.error(`Error flushing commands for system ${system.systemName}:`, error);
                }
            }
        }
        finally {
            ProfilerSDK.endSample(flushHandle);
        }
    }
    /**
     * 处理系统执行错误
     *
     * 记录错误信息并跟踪错误次数。当系统错误次数超过阈值时自动禁用该系统。
     *
     * @param system 出错的系统
     * @param phase 错误发生的阶段（update 或 lateUpdate）
     * @param error 错误对象
     */
    _handleSystemError(system, phase, error) {
        const errorCount = (this._systemErrorCount.get(system) || 0) + 1;
        this._systemErrorCount.set(system, errorCount);
        const name = system.systemName;
        this._logger.error(`Error in system ${name}.${phase}() [${errorCount}/${this._maxErrorCount}]:`, error);
        if (errorCount >= this._maxErrorCount) {
            system.enabled = false;
            this._logger.error(`System ${name} has been disabled due to excessive errors (${errorCount} errors)`);
        }
    }
    /**
     * 将实体添加到此场景，并返回它
     * @param name 实体名称
     */
    createEntity(name) {
        const entity = new Entity(name, this.identifierPool.checkOut());
        const handle = this.handleManager.create();
        entity.setHandle(handle);
        this._handleToEntity.set(handle, entity);
        this.eventSystem.emitSync('entity:created', { entityName: name, entity, scene: this });
        return this.addEntity(entity);
    }
    /**
     * 清除所有EntitySystem的实体缓存
     * 当实体或组件发生变化时调用
     */
    clearSystemEntityCaches() {
        for (const system of this.systems) {
            system.clearEntityCache();
        }
    }
    /**
     * 通知相关系统实体的组件发生了变化
     *
     * 这是事件驱动设计的核心：当组件被添加或移除时，立即通知相关系统检查该实体是否匹配，
     * 并触发 onAdded/onRemoved 回调。通过组件ID索引优化，只通知关心该组件的系统。
     *
     * This is the core of event-driven design: when a component is added or removed,
     * immediately notify relevant systems to check if the entity matches and trigger
     * onAdded/onRemoved callbacks. Optimized via component ID indexing to only notify
     * systems that care about the changed component.
     *
     * @param entity 组件发生变化的实体 | The entity whose components changed
     * @param changedComponentType 变化的组件类型（可选） | The changed component type (optional)
     */
    notifyEntityComponentChanged(entity, changedComponentType) {
        const notifiedSystems = new Set();
        if (changedComponentType && this.componentRegistry.isRegistered(changedComponentType)) {
            const componentId = this.componentRegistry.getBitIndex(changedComponentType);
            const interestedSystems = this._componentIdToSystems.get(componentId);
            if (interestedSystems) {
                for (const system of interestedSystems) {
                    system.handleEntityComponentChanged(entity);
                    notifiedSystems.add(system);
                }
            }
        }
        for (const system of this._globalNotifySystems) {
            if (!notifiedSystems.has(system)) {
                system.handleEntityComponentChanged(entity);
                notifiedSystems.add(system);
            }
        }
        if (!changedComponentType) {
            for (const system of this.systems) {
                if (!notifiedSystems.has(system)) {
                    system.handleEntityComponentChanged(entity);
                }
            }
        }
    }
    /**
     * 将系统添加到组件索引
     *
     * 根据系统的 Matcher 条件，将系统注册到相应的组件ID索引中。
     *
     * Index a system by its interested component types.
     * Registers the system to component ID indices based on its Matcher conditions.
     *
     * @param system 要索引的系统 | The system to index
     */
    indexSystemByComponents(system) {
        const matcher = system.matcher;
        if (!matcher) {
            return;
        }
        if (matcher.isNothing()) {
            return;
        }
        const condition = matcher.getCondition();
        if (condition.none.length > 0 || condition.tag !== undefined || condition.name !== undefined) {
            this._globalNotifySystems.add(system);
        }
        if (matcher.isEmpty()) {
            this._globalNotifySystems.add(system);
            return;
        }
        for (const componentType of condition.all) {
            this.addSystemToComponentIndex(componentType, system);
        }
        for (const componentType of condition.any) {
            this.addSystemToComponentIndex(componentType, system);
        }
        if (condition.component) {
            this.addSystemToComponentIndex(condition.component, system);
        }
    }
    /**
     * 将系统添加到指定组件的索引
     *
     * Add system to the index for a specific component type.
     *
     * @param componentType 组件类型 | Component type
     * @param system 系统 | System
     */
    addSystemToComponentIndex(componentType, system) {
        const componentId = this.componentRegistry.getBitIndex(componentType);
        let systems = this._componentIdToSystems.get(componentId);
        if (!systems) {
            systems = new Set();
            this._componentIdToSystems.set(componentId, systems);
        }
        systems.add(system);
    }
    /**
     * 从组件索引中移除系统
     *
     * Remove a system from all component indices.
     *
     * @param system 要移除的系统 | The system to remove
     */
    removeSystemFromIndex(system) {
        this._globalNotifySystems.delete(system);
        for (const systems of this._componentIdToSystems.values()) {
            systems.delete(system);
        }
    }
    /**
     * 在场景的实体列表中添加一个实体
     * @param entity 要添加的实体
     * @param deferCacheClear 是否延迟缓存清理（用于批量操作）
     */
    addEntity(entity, deferCacheClear = false) {
        this.entities.add(entity);
        entity.scene = this;
        this.querySystem.addEntity(entity, deferCacheClear);
        if (!deferCacheClear) {
            this.clearSystemEntityCaches();
        }
        this.eventSystem.emitSync('entity:added', { entity, scene: this });
        return entity;
    }
    /**
     * 批量创建实体（高性能版本）
     * @param count 要创建的实体数量
     * @param namePrefix 实体名称前缀
     * @returns 创建的实体列表
     */
    createEntities(count, namePrefix = 'Entity') {
        const entities = [];
        for (let i = 0; i < count; i++) {
            const entity = new Entity(`${namePrefix}_${i}`, this.identifierPool.checkOut());
            entity.scene = this;
            const handle = this.handleManager.create();
            entity.setHandle(handle);
            this._handleToEntity.set(handle, entity);
            entities.push(entity);
        }
        for (const entity of entities) {
            this.entities.add(entity);
        }
        this.querySystem.addEntitiesUnchecked(entities);
        this.eventSystem.emitSync('entities:batch_added', { entities, scene: this, count });
        return entities;
    }
    /**
     * 批量销毁实体
     */
    destroyEntities(entities) {
        if (entities.length === 0)
            return;
        for (const entity of entities) {
            entity.setDestroyedState(true);
        }
        for (const entity of entities) {
            entity.removeAllComponents();
        }
        for (const entity of entities) {
            this.entities.remove(entity);
            this.querySystem.removeEntity(entity);
            if (isValidHandle(entity.handle)) {
                this._handleToEntity.delete(entity.handle);
                this.handleManager.destroy(entity.handle);
            }
        }
        this.querySystem.clearCache();
        this.clearSystemEntityCaches();
    }
    /**
     * 从场景中删除所有实体
     */
    destroyAllEntities() {
        this.entities.removeAllEntities();
        this.querySystem.setEntities([]);
    }
    /**
     * 搜索并返回第一个具有名称的实体
     * @param name 实体名称
     */
    findEntity(name) {
        return this.entities.findEntity(name);
    }
    /**
     * 根据ID查找实体
     * @param id 实体ID
     */
    findEntityById(id) {
        return this.entities.findEntityById(id);
    }
    /**
     * 根据句柄查找实体
     *
     * 通过轻量级句柄查找对应的实体（O(1) 时间复杂度）。
     * 如果句柄无效或实体已被销毁，返回 null。
     *
     * Find entity by handle (O(1) time complexity).
     * Returns null if handle is invalid or entity has been destroyed.
     *
     * @param handle 实体句柄 | Entity handle
     * @returns 实体实例或 null | Entity instance or null
     */
    findEntityByHandle(handle) {
        if (!isValidHandle(handle) || !this.handleManager.isAlive(handle)) {
            return null;
        }
        return this._handleToEntity.get(handle) ?? null;
    }
    /**
     * 根据标签查找实体
     * @param tag 实体标签
     */
    findEntitiesByTag(tag) {
        const result = [];
        for (const entity of this.entities.buffer) {
            if (entity.tag === tag) {
                result.push(entity);
            }
        }
        return result;
    }
    /**
     * 查找所有持久化实体
     *
     * Find all persistent entities in this scene.
     *
     * @returns 持久化实体数组 | Array of persistent entities
     */
    findPersistentEntities() {
        return this.entities.buffer.filter(entity => entity.isPersistent);
    }
    /**
     * 提取持久化实体（从场景中分离但不销毁）
     *
     * 用于场景切换时收集需要迁移的实体。
     *
     * Extract persistent entities (detach from scene without destroying).
     * Used during scene transitions to collect entities for migration.
     *
     * @returns 被提取的持久化实体数组 | Array of extracted persistent entities
     *
     * @internal
     */
    extractPersistentEntities() {
        const persistentEntities = this.findPersistentEntities();
        for (const entity of persistentEntities) {
            this.entities.remove(entity);
            this.querySystem.removeEntity(entity);
            entity.scene = null; // detach but preserve component data
        }
        return persistentEntities;
    }
    /**
     * 接收迁移的实体
     *
     * 将从其他场景迁移来的实体添加到当前场景。
     *
     * Receive migrated entities from another scene.
     *
     * @param entities 要接收的实体数组 | Entities to receive
     *
     * @internal
     */
    receiveMigratedEntities(entities) {
        for (const entity of entities) {
            entity.scene = this;
            this.entities.add(entity);
            this.querySystem.addEntity(entity);
            for (const component of entity.components) {
                this.componentStorageManager.addComponent(entity.id, component);
                this.referenceTracker?.registerEntityScene(entity.id, this);
            }
        }
        if (entities.length > 0) {
            this.clearSystemEntityCaches();
        }
    }
    /**
     * 根据名称查找实体（别名方法）
     *
     * @param name 实体名称
     * @deprecated 请使用 findEntity() 代替此方法
     */
    getEntityByName(name) {
        return this.findEntity(name);
    }
    /**
     * 根据标签查找实体（别名方法）
     *
     * @param tag 实体标签
     * @deprecated 请使用 findEntitiesByTag() 代替此方法
     */
    getEntitiesByTag(tag) {
        return this.findEntitiesByTag(tag);
    }
    /**
     * 查询拥有所有指定组件的实体
     *
     * @param componentTypes - 组件类型数组
     * @returns 查询结果
     *
     * @example
     * ```typescript
     * const result = scene.queryAll(Position, Velocity);
     * for (const entity of result.entities) {
     *     const pos = entity.getComponent(Position);
     *     const vel = entity.getComponent(Velocity);
     * }
     * ```
     */
    queryAll(...componentTypes) {
        return this.querySystem.queryAll(...componentTypes);
    }
    /**
     * 查询拥有任意一个指定组件的实体
     *
     * @param componentTypes - 组件类型数组
     * @returns 查询结果
     */
    queryAny(...componentTypes) {
        return this.querySystem.queryAny(...componentTypes);
    }
    /**
     * 查询不包含指定组件的实体
     *
     * @param componentTypes - 组件类型数组
     * @returns 查询结果
     */
    queryNone(...componentTypes) {
        return this.querySystem.queryNone(...componentTypes);
    }
    /**
     * 创建类型安全的查询构建器
     *
     * @returns 查询构建器，支持链式调用
     *
     * @example
     * ```typescript
     * // 使用查询构建器
     * const matcher = scene.query()
     *     .withAll(Position, Velocity)
     *     .withNone(Disabled)
     *     .buildMatcher();
     *
     * // 在System中使用
     * class MovementSystem extends EntitySystem {
     *     constructor() {
     *         super(matcher);
     *     }
     * }
     * ```
     */
    query() {
        return new TypedQueryBuilder();
    }
    /**
     * 在场景中添加一个EntitySystem处理器
     *
     * 支持两种使用方式：
     * 1. 传入类型（推荐）：自动使用DI创建实例，支持@Injectable和@InjectProperty装饰器
     * 2. 传入实例：直接使用提供的实例
     *
     * @param systemTypeOrInstance 系统类型或系统实例
     * @returns 添加的处理器实例
     *
     * @example
     * ```typescript
     * // 方式1：传入类型，自动DI（推荐）
     * @Injectable()
     * class PhysicsSystem extends EntitySystem {
     *     @InjectProperty(CollisionSystem)
     *     private collision!: CollisionSystem;
     *
     *     constructor() {
     *         super(Matcher.empty().all(Transform));
     *     }
     * }
     * scene.addEntityProcessor(PhysicsSystem);
     *
     * // 方式2：传入实例
     * const system = new MySystem();
     * scene.addEntityProcessor(system);
     * ```
     */
    addEntityProcessor(systemTypeOrInstance) {
        let system;
        let constructor;
        if (typeof systemTypeOrInstance === 'function') {
            constructor = systemTypeOrInstance;
            if (this._services.isRegistered(constructor)) {
                const existingSystem = this._services.resolve(constructor);
                this._logger.debug(`System ${constructor.name} already registered, returning existing instance`);
                return existingSystem;
            }
            if (isInjectable(constructor)) {
                system = createInstance(constructor, this._services);
            }
            else {
                system = new constructor();
            }
        }
        else {
            system = systemTypeOrInstance;
            constructor = system.constructor;
            if (this._services.isRegistered(constructor)) {
                const existingSystem = this._services.resolve(constructor);
                if (existingSystem === system) {
                    this._logger.debug(`System ${constructor.name} instance already registered, returning it`);
                    return system;
                }
                else {
                    this._logger.warn(`Attempting to register a different instance of ${constructor.name}, ` +
                        'but type is already registered. Returning existing instance.');
                    return existingSystem;
                }
            }
        }
        system.scene = this;
        system.addOrder = this._systemAddCounter++; // for stable sorting
        system.setPerformanceMonitor(this.performanceMonitor);
        const metadata = getSystemMetadata(constructor);
        if (metadata?.updateOrder !== undefined) {
            system.setUpdateOrder(metadata.updateOrder);
        }
        if (metadata?.enabled !== undefined) {
            system.enabled = metadata.enabled;
        }
        this._services.registerInstance(constructor, system);
        this.markSystemsOrderDirty();
        this.indexSystemByComponents(system);
        injectProperties(system, this._services);
        // Auto-wrap system methods for profiling in debug mode
        if (ProfilerSDK.isEnabled()) {
            AutoProfiler.wrapInstance(system, system.systemName, ProfileCategory.ECS);
        }
        system.initialize();
        this._logger.debug(`System ${constructor.name} registered and initialized`);
        return system;
    }
    /**
     * 批量注册EntitySystem到场景（使用DI）
     *
     * 自动按照依赖顺序注册多个System。
     * 所有System必须使用@Injectable装饰器标记。
     *
     * @param systemTypes System类型数组
     * @returns 注册的System实例数组
     *
     * @example
     * ```typescript
     * @Injectable()
     * @ECSSystem('Collision', { updateOrder: 5 })
     * class CollisionSystem extends EntitySystem implements IService {
     *     constructor() { super(Matcher.empty().all(Collider)); }
     *     dispose() {}
     * }
     *
     * @Injectable()
     * @ECSSystem('Physics', { updateOrder: 10 })
     * class PhysicsSystem extends EntitySystem implements IService {
     *     @InjectProperty(CollisionSystem)
     *     private collision!: CollisionSystem;
     *
     *     constructor() {
     *         super(Matcher.empty().all(Transform, RigidBody));
     *     }
     *     dispose() {}
     * }
     *
     * // 批量注册（自动解析依赖顺序）
     * scene.registerSystems([
     *     CollisionSystem,
     *     PhysicsSystem,  // 自动注入CollisionSystem
     *     RenderSystem
     * ]);
     * ```
     */
    registerSystems(systemTypes) {
        const registeredSystems = [];
        for (const systemType of systemTypes) {
            const system = this.addEntityProcessor(systemType);
            registeredSystems.push(system);
        }
        return registeredSystems;
    }
    /**
     * 添加系统到场景（addEntityProcessor的别名）
     * @param system 系统
     */
    addSystem(system) {
        return this.addEntityProcessor(system);
    }
    /**
     * 从场景中删除EntitySystem处理器
     * @param processor 要删除的处理器
     */
    removeEntityProcessor(processor) {
        const constructor = processor.constructor;
        this._services.unregister(constructor);
        this.markSystemsOrderDirty();
        this.removeSystemFromIndex(processor);
        processor.reset();
    }
    /**
     * 从场景中删除系统（removeEntityProcessor的别名）
     * @param system 系统
     */
    removeSystem(system) {
        this.removeEntityProcessor(system);
    }
    /**
     * 获取指定类型的EntitySystem处理器
     *
     * @deprecated 推荐使用依赖注入代替此方法。使用 `scene.services.resolve(SystemType)` 或使用 `@InjectProperty(SystemType)` 装饰器。
     *
     * @param type 处理器类型
     * @returns 处理器实例，如果未找到则返回null
     *
     * @example
     * ```typescript
     * @Injectable()
     * class MySystem extends EntitySystem {
     *     @InjectProperty(PhysicsSystem)
     *     private physics!: PhysicsSystem;
     *
     *     constructor() {
     *         super(Matcher.empty());
     *     }
     * }
     * ```
     */
    getEntityProcessor(type) {
        return this._services.tryResolve(type);
    }
    /**
     * 获取场景统计信息
     */
    getStats() {
        return {
            entityCount: this.entities.count,
            processorCount: this.systems.length,
            componentStorageStats: this.componentStorageManager.getAllStats()
        };
    }
    /**
     * 获取场景的调试信息
     */
    getDebugInfo() {
        const systems = this.systems;
        return {
            name: this.name || this.constructor.name,
            entityCount: this.entities.count,
            processorCount: systems.length,
            isRunning: this._didSceneBegin,
            entities: this.entities.buffer.map((entity) => ({
                name: entity.name,
                id: entity.id,
                componentCount: entity.components.length,
                componentTypes: entity.components.map((c) => getComponentInstanceTypeName(c))
            })),
            processors: systems.map((processor) => ({
                name: getSystemInstanceTypeName(processor),
                updateOrder: processor.updateOrder,
                entityCount: processor.entities.length
            })),
            componentStats: this.componentStorageManager.getAllStats()
        };
    }
    /**
     * 序列化场景
     *
     * 将场景及其所有实体、组件序列化为JSON字符串或二进制Uint8Array
     *
     * @param options 序列化选项
     * @returns 序列化后的数据（JSON字符串或二进制Uint8Array）
     *
     * @example
     * ```typescript
     * // JSON格式
     * const jsonData = scene.serialize({
     *     format: 'json',
     *     pretty: true
     * });
     *
     * // 二进制格式（更小、更快）
     * const binaryData = scene.serialize({
     *     format: 'binary'
     * });
     * ```
     */
    serialize(options) {
        return SceneSerializer.serialize(this, options);
    }
    /**
     * 反序列化场景
     *
     * 从序列化数据恢复场景状态
     *
     * @param saveData 序列化的数据（JSON字符串或二进制Uint8Array）
     * @param options 反序列化选项
     *
     * @example
     * ```typescript
     * // 从JSON恢复（自动从ComponentRegistry获取组件类型）
     * scene.deserialize(jsonData, {
     *     strategy: 'replace'
     * });
     *
     * // 从二进制恢复
     * scene.deserialize(binaryData, {
     *     strategy: 'replace'
     * });
     * ```
     */
    deserialize(saveData, options) {
        SceneSerializer.deserialize(this, saveData, options);
    }
    /**
     * 创建增量序列化的基础快照
     *
     * 在需要进行增量序列化前，先调用此方法创建基础快照
     *
     * @param options 序列化选项
     *
     * @example
     * ```typescript
     * // 创建基础快照
     * scene.createIncrementalSnapshot();
     *
     * // 进行一些修改...
     * entity.addComponent(new PositionComponent(100, 200));
     *
     * // 计算增量变更
     * const incremental = scene.serializeIncremental();
     * ```
     */
    createIncrementalSnapshot(options) {
        this._incrementalBaseSnapshot = IncrementalSerializer.createSnapshot(this, options);
    }
    /**
     * 增量序列化场景
     *
     * 只序列化相对于基础快照的变更部分
     *
     * @param options 序列化选项
     * @returns 增量快照对象
     *
     * @example
     * ```typescript
     * // 创建基础快照
     * scene.createIncrementalSnapshot();
     *
     * // 修改场景
     * const entity = scene.createEntity('NewEntity');
     * entity.addComponent(new PositionComponent(50, 100));
     *
     * // 获取增量变更
     * const incremental = scene.serializeIncremental();
     * console.log(`变更数量: ${incremental.entityChanges.length}`);
     *
     * // 序列化为JSON
     * const json = IncrementalSerializer.serializeIncremental(incremental);
     * ```
     */
    serializeIncremental(options) {
        if (!this._incrementalBaseSnapshot) {
            throw new Error('必须先调用 createIncrementalSnapshot() 创建基础快照');
        }
        return IncrementalSerializer.computeIncremental(this, this._incrementalBaseSnapshot, options);
    }
    /**
     * 应用增量变更到场景
     *
     * @param incremental 增量快照数据（IncrementalSnapshot对象、JSON字符串或二进制Uint8Array）
     * @param componentRegistry 组件类型注册表（可选，默认使用全局注册表）
     *
     * @example
     * ```typescript
     * // 应用增量变更对象
     * scene.applyIncremental(incrementalSnapshot);
     *
     * // 从JSON字符串应用
     * const jsonData = IncrementalSerializer.serializeIncremental(snapshot, { format: 'json' });
     * scene.applyIncremental(jsonData);
     *
     * // 从二进制Uint8Array应用
     * const binaryData = IncrementalSerializer.serializeIncremental(snapshot, { format: 'binary' });
     * scene.applyIncremental(binaryData);
     * ```
     */
    applyIncremental(incremental, componentRegistry) {
        const isSerializedData = typeof incremental === 'string' || incremental instanceof Uint8Array;
        const snapshot = isSerializedData
            ? IncrementalSerializer.deserializeIncremental(incremental)
            : incremental;
        const registry = componentRegistry || this.componentRegistry.getAllComponentNames();
        IncrementalSerializer.applyIncremental(this, snapshot, registry);
    }
    /**
     * 更新增量快照基准
     *
     * 将当前场景状态设为新的增量序列化基准
     *
     * @param options 序列化选项
     *
     * @example
     * ```typescript
     * // 创建初始快照
     * scene.createIncrementalSnapshot();
     *
     * // 进行一些修改并序列化
     * const incremental1 = scene.serializeIncremental();
     *
     * // 更新基准，之后的增量将基于当前状态
     * scene.updateIncrementalSnapshot();
     *
     * // 继续修改
     * const incremental2 = scene.serializeIncremental();
     * ```
     */
    updateIncrementalSnapshot(options) {
        this.createIncrementalSnapshot(options);
    }
    /**
     * 清除增量快照
     *
     * 释放快照占用的内存
     */
    clearIncrementalSnapshot() {
        this._incrementalBaseSnapshot = undefined;
    }
    /**
     * 检查是否有增量快照
     *
     * @returns 如果已创建增量快照返回true
     */
    hasIncrementalSnapshot() {
        return this._incrementalBaseSnapshot !== undefined;
    }
}
//# sourceMappingURL=Scene.js.map